import type { QueryClient } from '@tanstack/react-query';
import {
  CrmFunnelsConversationsService,
  SearcherDocumentSearchFilterOperator,
  type FunnelConversation,
  type FunnelConversationDocument,
} from 'src/api/generated';
import { ApiError } from 'src/api/generated/core/ApiError';

import { hasDeletedMessagePreview } from '../utils/cache/funnel-conversations/deleted-message-preview.util';
import { refreshConversationSearch } from './conversation-search-refresh.service';

const DOCUMENT_QUERY_KEY = ['crm/documents/funnel-conversations'];
const RECOVERY_WINDOW_MS = 30_000;
const REQUEST_TIMEOUT_MS = 5_000;

const hasActiveDocuments = (queryClient: QueryClient): boolean =>
  queryClient.getQueryCache().findAll({
    queryKey: DOCUMENT_QUERY_KEY,
    type: 'active',
  }).length > 0;

const hasIndexedRevision = (
  document: FunnelConversationDocument,
  conversation: FunnelConversation
): boolean =>
  document.id === String(conversation.id) &&
  document.tenantId === conversation.tenantId &&
  Date.parse(document.updatedAt) >= Date.parse(conversation.updatedAt) &&
  String(document.funnelId ?? '') === String(conversation.funnelId) &&
  String(document.funnelStatusId ?? '') ===
    String(conversation.funnelStatusId ?? '') &&
  String(document.userId ?? '') === String(conversation.userId ?? '') &&
  String(document.propertyId ?? '') === String(conversation.propertyId ?? '') &&
  (document.archivedAt ?? null) === (conversation.archivedAt ?? null);

/**
 * Refreshes active search views once indexing reaches the latest canonical state.
 * React Query coalesces concurrent requests for one tenant/conversation. This
 * best-effort recovery never fabricates search rows or replaces polling recovery.
 *
 * @param queryClient - Cache owning the canonical conversation and search views.
 * @param conversation - Accepted canonical event that requires search recovery.
 * @returns Completion of this attempt, or immediately when recovery already runs.
 */
export const recoverConversationDocuments = async (
  queryClient: QueryClient,
  conversation: FunnelConversation
): Promise<void> => {
  if (!hasActiveDocuments(queryClient)) return;
  const queryKey = [
    'crm/conversation-document-recovery',
    conversation.tenantId,
    conversation.id,
  ];
  const state = queryClient.getQueryState(queryKey);
  if (state && state.fetchStatus !== 'idle') return;
  const deadline = Date.now() + RECOVERY_WINDOW_MS;
  try {
    await queryClient.fetchQuery({
      queryKey,
      gcTime: 0,
      staleTime: 0,
      retry: (failures, error) =>
        failures < 5 &&
        Date.now() < deadline &&
        hasActiveDocuments(queryClient) &&
        !(error instanceof ApiError && [401, 403].includes(error.status)),
      retryDelay: (attempt) =>
        Math.min(
          1_000 * 2 ** attempt,
          8_000,
          Math.max(0, deadline - Date.now())
        ),
      queryFn: async ({ signal }) => {
        if (
          signal.aborted ||
          !hasActiveDocuments(queryClient) ||
          Date.now() >= deadline
        ) {
          return false;
        }
        const request =
          CrmFunnelsConversationsService.searchFunnelConversations({
            filters: [
              {
                field: 'id',
                operator: SearcherDocumentSearchFilterOperator.EQ,
                value: String(conversation.id),
              },
            ],
            size: 1,
            includeArchived: true,
          });
        const cancel = () => request.cancel();
        signal.addEventListener('abort', cancel, { once: true });
        const timeout = window.setTimeout(
          cancel,
          Math.min(REQUEST_TIMEOUT_MS, deadline - Date.now())
        );
        try {
          const response = await request;
          const cached = queryClient.getQueryData<FunnelConversation>([
            'crm/funnel-conversations',
            conversation.id,
          ]);
          if (!cached || cached.tenantId !== conversation.tenantId) {
            return false;
          }
          // A late HTTP result must never lower this accepted event's target.
          const latest =
            Date.parse(cached.updatedAt) < Date.parse(conversation.updatedAt)
              ? conversation
              : cached;
          if (
            !response.items.some(
              (item) =>
                hasIndexedRevision(item, latest) &&
                !hasDeletedMessagePreview(queryClient, item)
            )
          ) {
            throw new Error('Conversation search index has not caught up');
          }
          if (hasActiveDocuments(queryClient)) {
            await refreshConversationSearch(queryClient);
          }
          const current = queryClient.getQueryData<FunnelConversation>([
            'crm/funnel-conversations',
            conversation.id,
          ]);
          if (current && current.updatedAt !== latest.updatedAt) {
            throw new Error('Conversation advanced during search recovery');
          }
          return true;
        } finally {
          window.clearTimeout(timeout);
          signal.removeEventListener('abort', cancel);
        }
      },
    });
  } catch {
    // Optional recovery must not surface external errors or undo accepted state.
    // Existing polling, visibility and reconnect recovery remain available.
  }
};
